"use client";

import type { WorkspaceFileItem } from "@/app/api/__generated__/models/workspaceFileItem";
import { Text } from "@/components/atoms/Text/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from "@/components/atoms/Tooltip/BaseTooltip";
import { cn } from "@/lib/utils";
import { PencilEdit02Icon, Tick02Icon } from "@hugeicons/core-free-icons";
import { Icon } from "@/components/atoms/Icon/Icon";
import { motion, useReducedMotion } from "framer-motion";
import { useState } from "react";
import { RenameFileDialog } from "../../RenameFileDialog/RenameFileDialog";
import { useFileDrag } from "../../WorkspaceFolders/useFileDrag";
import { ExpertBadge } from "../ExpertBadge";
import { FileActionsMenu } from "../FileActionsMenu";
import { formatDayLabel, formatFileSize, formatFullDate } from "../helpers";
import { FilePreviewCard } from "./FilePreviewCard";
import { FileThumbnail } from "./FileThumbnail";
import {
  ACTIONS_CELL_CLASS,
  NAME_BUTTON_CLASS,
  REDUCED_ROW_VARIANTS,
  ROW_GRID_CLASS,
  ROW_VARIANTS,
} from "./row-layout";

interface Props {
  file: WorkspaceFileItem;
  onOpen: (file: WorkspaceFileItem) => void;
  /** Position in the list; drives the small entrance stagger. */
  index?: number;
  isSelected: boolean;
  /** Every selected file id; a drag from a selected row carries all of them. */
  selectedIds: string[];
  onToggleSelect: (file: WorkspaceFileItem) => void;
}

// Long enough that skimming down the list doesn't flash previews; short
// enough that a deliberate pause shows one. Moving between rows within the
// provider's skip delay opens the next preview immediately.
const PREVIEW_DELAY_MS = 450;

export function FileRow({
  file,
  onOpen,
  index = 0,
  isSelected,
  selectedIds,
  onToggleSelect,
}: Props) {
  const reduceMotion = useReducedMotion();
  const dragIds = isSelected ? selectedIds : [file.id];
  const dragLabel = dragIds.length > 1 ? `${dragIds.length} files` : file.name;
  const { handleDragStart, handleDragEnd } = useFileDrag(dragIds, dragLabel);
  const [isRenameOpen, setIsRenameOpen] = useState(false);

  return (
    <motion.li
      variants={reduceMotion ? REDUCED_ROW_VARIANTS : ROW_VARIANTS}
      custom={index}
      initial={reduceMotion ? false : "hidden"}
      animate="show"
      className={cn(
        ROW_GRID_CLASS,
        "group cursor-pointer px-2 transition-colors hover:bg-surface-muted has-[[data-state=open]]:bg-surface-muted",
        isSelected && "bg-surface-hover hover:bg-surface-hover",
      )}
      data-testid="artifacts-list-item"
      data-selected={isSelected || undefined}
      draggable
      onClick={() => onOpen(file)}
      onDragStartCapture={handleDragStart}
      onDragEndCapture={handleDragEnd}
    data-sentry-element="motion.li" data-sentry-component="FileRow" data-sentry-source-file="FileRow.tsx">
      {/* The thumbnail toggles the row's selection; the name is the row's
          accessible open control: it takes focus, Enter bubbles a click up to
          the row, and hovering (or focusing) it opens the large preview. The
          rename pencil sits beside it, outside the preview trigger. */}
      <div className="flex min-w-0 items-center gap-1 justify-self-start">
        <button
          type="button"
          aria-label={`Select ${file.name}`}
          aria-pressed={isSelected}
          className="group/select relative my-2.5 mr-3 shrink-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-foreground-subtle"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(file);
          }}
          data-testid="artifacts-select"
        >
          <FileThumbnail file={file} data-sentry-element="FileThumbnail" data-sentry-source-file="FileRow.tsx" />
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl text-action-foreground transition-opacity",
              isSelected
                ? "bg-action-hover/80 opacity-100"
                : "bg-action-hover/60 opacity-0 group-hover/select:opacity-100 group-focus-visible/select:opacity-100",
            )}
          >
            <Icon icon={Tick02Icon} size={20} data-sentry-element="Icon" data-sentry-source-file="FileRow.tsx" />
          </span>
        </button>
        <Tooltip delayDuration={PREVIEW_DELAY_MS} data-sentry-element="Tooltip" data-sentry-source-file="FileRow.tsx">
          <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="FileRow.tsx">
            <button
              type="button"
              className={NAME_BUTTON_CLASS}
              data-testid="artifacts-card-open"
            >
              <Text
                variant="body-medium"
                as="span"
                tone="primary"
                className="truncate"
              data-sentry-element="Text" data-sentry-source-file="FileRow.tsx">
                {file.name}
              </Text>
              <ExpertBadge expertId={file.expert_id} className="shrink-0" data-sentry-element="ExpertBadge" data-sentry-source-file="FileRow.tsx" />
            </button>
          </TooltipTrigger>
          <TooltipPortal data-sentry-element="TooltipPortal" data-sentry-source-file="FileRow.tsx">
            {/* aria-label keeps Radix from mirroring the whole card into its
              visually-hidden tooltip copy (which would fetch previews twice). */}
            <TooltipContent
              aria-label={`Preview of ${file.name}`}
              side="right"
              align="center"
              sideOffset={16}
              collisionPadding={16}
              // oxlint-disable-next-line design-system/no-restyle -- the hover preview is a card, which the tooltip atom has no variant for
              className="max-w-none rounded-2xl border border-border-base bg-surface p-0 text-body text-foreground-strong shadow-xl shadow-scrim/10 outline-none"
            data-sentry-element="TooltipContent" data-sentry-source-file="FileRow.tsx">
              <FilePreviewCard file={file} data-sentry-element="FilePreviewCard" data-sentry-source-file="FileRow.tsx" />
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
        <button
          type="button"
          aria-label={`Rename ${file.name}`}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-foreground-subtle opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surface-hover hover:text-foreground-strong focus-visible:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            setIsRenameOpen(true);
          }}
          data-testid="artifacts-rename"
        >
          <Icon icon={PencilEdit02Icon} size={16} data-sentry-element="Icon" data-sentry-source-file="FileRow.tsx" />
        </button>
      </div>
      <Text
        variant="body"
        as="span"
        tone="muted"
        className="hidden truncate sm:block"
        title={formatFullDate(file.created_at)}
      data-sentry-element="Text" data-sentry-source-file="FileRow.tsx">
        {formatDayLabel(file.created_at)}
      </Text>
      <Text
        variant="body"
        as="span"
        tone="muted"
        tabular
        className="hidden md:block"
      data-sentry-element="Text" data-sentry-source-file="FileRow.tsx">
        {formatFileSize(file.size_bytes)}
      </Text>
      <div
        className={cn(
          ACTIONS_CELL_CLASS,
          "opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <FileActionsMenu file={file} data-sentry-element="FileActionsMenu" data-sentry-source-file="FileRow.tsx" />
      </div>
      {isRenameOpen && (
        // The dialog portals out of the row in the DOM, but React events still
        // bubble through the component tree, so its clicks must not reach the
        // row's open handler.
        <div onClick={(e) => e.stopPropagation()}>
          <RenameFileDialog
            file={file}
            isOpen={isRenameOpen}
            setIsOpen={setIsRenameOpen}
          />
        </div>
      )}
    </motion.li>
  );
}
